import { useState, useCallback } from 'react';

export type ColumnFilters = Record<string, string[]>;

export interface UseTableFiltersReturn {
  columnFilters: ColumnFilters;
  setColumnFilters: (filters: ColumnFilters | ((prev: ColumnFilters) => ColumnFilters)) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

/**
 * Hook for managing table column filters
 * Used with Spreadsheet/SpreadsheetPlain components
 * 
 * @param initialFilters - Initial filter state (optional)
 * @returns Filter state and management functions
 * 
 * @example
 * const { columnFilters, setColumnFilters, clearFilters, hasActiveFilters } = useTableFilters();
 * 
 * <Spreadsheet
 *   columnFilters={columnFilters}
 *   onColumnFiltersChange={setColumnFilters}
 * />
 */
export const useTableFilters = (initialFilters: ColumnFilters = {}): UseTableFiltersReturn => {
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>(initialFilters);

  const clearFilters = useCallback(() => {
    setColumnFilters({});
  }, []);

  const hasActiveFilters = Object.keys(columnFilters).length > 0 && 
    Object.values(columnFilters).some(filters => filters.length > 0);

  return {
    columnFilters,
    setColumnFilters,
    clearFilters,
    hasActiveFilters,
  };
};
